/**
 * Host-derived game-API base, the REST twin of getSocketUrl (useSocketManager).
 *
 * The widget is served from rotating mirror domains (game.loop-cast.click, ...)
 * so that players whose ISPs block the primary domain can still play. The
 * socket host has always followed the page host; the REST base was pinned to
 * NEXT_PUBLIC_GAME_API_URL, which left the crash manual cash-out — the only
 * browser call to the game API — dead on exactly the ISPs the mirrors exist
 * for (hub88 SF-002189753).
 *
 * Derivation swaps only the zone: the api subdomain comes from the env base's
 * own host and the path/scheme are preserved, so
 *   game.loop-cast.click + https://api.marbles.xyz/game
 *     → https://api.loop-cast.click/game
 *
 * Only hosts shaped like our own rotation domains (first label "game") derive;
 * white-labels on operator-owned zones (v2.stakes.to) keep the env base — we
 * cannot create api.* records in zones we don't host. Rotation zones must have
 * api.<zone> provisioned (terragrunt delivery unit, mirror_api_zones) — until
 * they do, callers fall back to the env base on network errors.
 */
export function getGameApiBase(
  hostname: string | undefined,
  envBase: string | undefined
): string | undefined {
  if (!envBase) return undefined

  // Server-side rendering callers pass the request host; no host → env var.
  if (!hostname) return envBase

  // window.location.hostname is always lowercase, but layout.tsx passes the
  // raw Host header, which clients may send in any case.
  hostname = hostname.toLowerCase()

  // Local development, previews, raw IPs — same guard list as getSocketUrl.
  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname.startsWith('192.168.') ||
    hostname.endsWith('.vercel.app') ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  ) {
    return envBase
  }

  const parts = hostname.split('.')
  if (parts.length < 2 || parts[0] !== 'game') {
    return envBase
  }
  const zone = parts.slice(1).join('.')

  try {
    const url = new URL(envBase)
    const apiPrefix = url.hostname.split('.')[0]
    const derivedHost = `${apiPrefix}.${zone}`
    if (derivedHost === url.hostname) {
      // Primary domain — the derived host IS the env base's host.
      return envBase
    }
    url.hostname = derivedHost
    return url.toString().replace(/\/$/, '')
  } catch {
    // Malformed env value — hand it back unchanged rather than invent a URL.
    return envBase
  }
}

/**
 * Ordered, de-duplicated bases for the cashout POST: the host-derived base
 * first (reachable on blocked ISPs once api.<zone> is provisioned), the env
 * base as fallback (covers zones rotated in before their api record exists —
 * a network error there is fast NXDOMAIN, not a hang). Empty when the env var
 * is missing so the caller can raise its existing config error.
 */
export function getCashoutApiBases(
  hostname: string | undefined,
  envBase: string | undefined
): string[] {
  if (!envBase) return []
  const derived = getGameApiBase(hostname, envBase)
  return derived && derived !== envBase ? [derived, envBase] : [envBase]
}
